import { useState } from "react";

type DialogKey =
  | "settingsMenu"
  | "search"
  | "projectMenu"
  | "addElementMenu"
  | "selectionToolsMenu"
  | "toolsMenu"
  | "georeference"
  | "options"
  | "projectDataMenu"
  | "backgroundImage"
  | "simulationSummary"
  | "elementDefaults"
  | "groupEdit"
  | "statisticsReport"
  | "gisImport"
  | "mapSettingsMenu"
  | "externalFiles";

const INITIAL: Record<DialogKey, boolean> = {
  settingsMenu: false,
  search: false,
  projectMenu: false,
  addElementMenu: false,
  selectionToolsMenu: false,
  toolsMenu: false,
  georeference: false,
  options: false,
  projectDataMenu: false,
  backgroundImage: false,
  simulationSummary: false,
  elementDefaults: false,
  groupEdit: false,
  statisticsReport: false,
  gisImport: false,
  mapSettingsMenu: false,
  externalFiles: false,
};

/**
 * Plain open/closed flags for the app's simplest popovers and dialogs, as
 * one shared `Record` instead of a separate `useState` per dialog.
 * Deliberately NOT used for the three map overlays (useResultsAnimation/
 * useThematicMap/useMapQuery): those need to close each other on open, which
 * a single shared Record would make easy to get wrong.
 */
export function useDialogToggles() {
  const [open, setOpen] = useState<Record<DialogKey, boolean>>(INITIAL);

  function toggle(key: DialogKey) {
    setOpen((current) => ({ ...current, [key]: !current[key] }));
  }

  /** Same as `toggle`, but atomically closes every other key in `group` when `key` is about to open — for the toolbar dropdown menus, since each has its own close-on-leave grace delay (useCloseOnLeave.ts) and a sibling can otherwise still be open when another is clicked. */
  function toggleExclusive(key: DialogKey, group: readonly DialogKey[]) {
    setOpen((current) => {
      if (current[key]) return { ...current, [key]: false };
      const next = { ...current };
      for (const k of group) next[k] = false;
      next[key] = true;
      return next;
    });
  }

  /** Opens `key` unconditionally (idempotent if already open) — for toolbar buttons that only ever open a dialog, never toggle it shut again from the same button. */
  function openDialog(key: DialogKey) {
    setOpen((current) => (current[key] ? current : { ...current, [key]: true }));
  }

  function close(key: DialogKey) {
    setOpen((current) => (current[key] ? { ...current, [key]: false } : current));
  }

  return { open, toggle, toggleExclusive, openDialog, close };
}
